import { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { FaArrowRight, FaTools } from 'react-icons/fa';
import {
  SERVICES_PAGE_COPY,
  getPracticeBySlug,
  getPracticeSeo,
  hubServiceOrder,
  practiceHasCertifications,
  servicePath,
} from '../../content/services';
import { CERTIFICATIONS, SHOW_CERTIFICATIONS } from '../../content/certifications';
import CTASection from '../../components/CTASection/CTASection';
import CertificationBadges from '../../components/CertificationBadges/CertificationBadges';
import ProofBar from '../../components/ProofBar/ProofBar';
import Seo, { seoPropsFor } from '../../components/Seo/Seo';
import { SERVICE_ICONS } from './ServiceDetail';
import styles from './Services.module.scss';

/**
 * One hub component, both practices: /services/servicenow and
 * /services/cloud-ai.
 *
 * Hero, service cards, proof slot, CTA -- the same skeleton for both, and the
 * same routing shape as /services itself.
 *
 * A "How We Work" section used to sit between the cards and the CTA. It was
 * removed: it collided by name with "How Engagements Work" one click back on
 * /services, half its copy was identical on both hubs, and it described the
 * company rather than the practice.
 *
 * The certification band and the proof slot are the two optional sections.
 * Both are empty right now, and each renders no section element at all rather
 * than an empty frame.
 */
const PracticeHubPage = () => {
  const { practiceSlug } = useParams<{ practiceSlug: string }>();
  const practice = practiceSlug ? getPracticeBySlug(practiceSlug) : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [practiceSlug]);

  if (!practice) {
    return <Navigate to="/services" replace />;
  }

  // The full list on a certification-backed practice, not just the codes its
  // own services reference. Switched off site-wide right now, so no hub
  // renders it. See content/certifications.ts.
  const showsCertifications = SHOW_CERTIFICATIONS && practiceHasCertifications(practice);

  return (
    <div className={styles.servicesPage}>
      <Seo {...seoPropsFor(getPracticeSeo(practice))} />

      <div className="main-content">
        <section className={styles.practiceHubHero}>
          <div className={styles.container}>
            <h1>{practice.name}</h1>
            <p className={styles.practiceHubIntro}>{practice.positioning}</p>
          </div>
        </section>

        {showsCertifications && (
          <section className={styles.certificationBand}>
            <div className={styles.container}>
              <CertificationBadges
                certifications={CERTIFICATIONS}
                label={SERVICES_PAGE_COPY.certificationsLabel}
              />
            </div>
          </section>
        )}

        <section className={styles.practiceHubServices}>
          <div className={styles.container}>
            {/* "Solutions" rather than "Services": on a hub that is already
                reached from a Services nav item, repeating the word names the
                route rather than what is on offer. */}
            <h2>Solutions</h2>
            <div className={styles.categoriesGrid}>
              {/* ITSM, CMDB, and Integrations & Automation lead: they have
                  both a certification and delivered work behind them. */}
              {hubServiceOrder(practice).map((service) => {
                const Icon = SERVICE_ICONS[service.iconKey] || FaTools;
                return (
                  <Link
                    to={servicePath(service)}
                    key={service.slug}
                    className={styles.categoryCard}
                  >
                    <div className={styles.categoryIcon}>
                      <Icon />
                    </div>
                    <h3>{service.navLabel}</h3>
                    <p className={styles.cardSummary}>{service.summary}</p>
                    {SHOW_CERTIFICATIONS && service.certification && (
                      <p className={styles.cardCertification}>
                        Backed by {service.certification}
                      </p>
                    )}
                    {/* The whole card is the link, so this is an affordance
                        rather than a second target -- it tells the eye the
                        card goes somewhere. Hidden from assistive tech: the
                        link is already named by its heading and summary, and
                        eleven identical "Learn more" links read as noise. */}
                    <span className={styles.cardCta} aria-hidden="true">
                      Learn more
                      <FaArrowRight />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Proof slot. Null on both practices today, so nothing renders --
            no section element, no empty band. It appears on its own once a
            real, attributable number exists. */}
        {practice.proofSlot && (
          <section className={styles.proofSection}>
            <div className={styles.container}>
              <ProofBar
                items={[
                  {
                    value: practice.proofSlot.stat,
                    label: practice.proofSlot.context,
                  },
                ]}
              />
            </div>
          </section>
        )}

        <CTASection
          heading={`Talk to us about ${practice.name}`}
          body="Tell us about your current environment and your priorities. We will recommend which of these services fits."
          ctaLabel="Contact Us"
          ctaHref="/contact"
        />
      </div>
    </div>
  );
};

export default PracticeHubPage;
